/**
 *
 * FNAnalysisFNStatementsIncomeStatement
 *
 */

import React, { Component } from "react";

import { getRequest } from "../api/ApiService";

import HelpIconButtonWithTooltipAndTypeformModal from "../components/typeform/HelpIconButtonWithTooltipAndTypeformModal";
import { logError } from "../services/utils";
import TypeformContent from "../components/typeform/TypeformContent";

// Components

/**
 * Price in millions.
 */
const PriceInMillions = ({ price, baseClassName }) => {
  if (typeof price !== "number") {
    return <div className={baseClassName}>n/a</div>;
  }

  let newPrice;
  if (price < 0) {
    newPrice = (price / 1000000).toFixed(2) * -1;
    return (
      <div className={`font-red ${baseClassName}`}>{`-$${newPrice}M`}</div>
    );
  } else {
    newPrice = (price / 1000000).toFixed(2);
    return <div className={baseClassName}>{`$${newPrice}M`}</div>;
  }
};

/**
 * Table row contents
 */

const TableHead = ({ rounds }) => (
  <thead>
    <tr>
      <th className="table-title text-center">
        <span />
      </th>
      {rounds.map((round, i) => (
        <th key={i} className="table-title title-gray text-center">
          YEAR {round.number}
        </th>
      ))}
    </tr>
  </thead>
);

/**
 * Table row contents
 */

const TableRowContents = ({ incomeStatement, name }) => (
  <tr className="text-center">
    <th className="description">{name ? name : incomeStatement.name}</th>
    {Object.keys(incomeStatement.data).map(round_number => {
      let data = incomeStatement.data[round_number];
      return (
        <td key={round_number}>
          <PriceInMillions baseClassName="text-center" price={data.value} />
        </td>
      );
    })}
  </tr>
);

const OtherGnACosts = ({ gnaCosts, depExpense, name }) => {
  const total = {};

  Object.keys(gnaCosts.data).forEach(round_number => {
    if (!(round_number in total)) {
      total[round_number] = 0;
    }

    total[round_number] += gnaCosts.data[round_number].value;
  });

  Object.keys(depExpense.data).forEach(round_number => {
    if (!(round_number in total)) {
      total[round_number] = 0;
    }

    total[round_number] += depExpense.data[round_number].value;
  });

  return (
    <tr className="text-center">
      <th className="description">{name ? name : ""}</th>
      {Object.keys(total).map(round_number => (
        <td key={round_number}>
          <PriceInMillions
            baseClassName="text-center"
            price={total[round_number]}
          />
        </td>
      ))}
    </tr>
  );
};

/**
 * Table row contents with gray background color.
 */

const TableRowContentsFooter = ({ incomeStatement }) => (
  <tr className="text-center bg-gray none-shadow">
    <th className="description">
      <strong>
        {incomeStatement.name}
        {incomeStatement.name === "Gross Profit" && (
          <HelpIconButtonWithTooltipAndTypeformModal
            iconSize="sm"
            {...TypeformContent.GROSS_PROFITS}
          />
        )}
      </strong>
    </th>
    {Object.keys(incomeStatement.data).map(round_number => {
      let data = incomeStatement.data[round_number];
      return (
        <td key={round_number}>
          <PriceInMillions baseClassName="text-center" price={data.value} />
        </td>
      );
    })}
  </tr>
);

/**
 * Table row total content
 */

const TotalComponent = ({ incomeStatement, title }) => (
  <tr className="text-center bg-gray none-shadow">
    <th className="description">
      <strong>
        {title ? title : incomeStatement.name}
        {title === "Net Profits" && (
          <HelpIconButtonWithTooltipAndTypeformModal
            iconSize="sm"
            {...TypeformContent.NET_PROFITS}
          />
        )}
      </strong>
    </th>
    {Object.keys(incomeStatement.data).map(round_number => {
      let data = incomeStatement.data[round_number];
      return (
        <td key={round_number}>
          <PriceInMillions baseClassName="text-center" price={data.value} />
        </td>
      );
    })}
  </tr>
);

/**
 * Table row operating profit
 */

const OperatingProfitComponent = ({ totalOperatingExpenses, grossProfits }) => (
  <tr className="text-center bg-gray none-shadow">
    <th className="description">
      <strong>Operation Profits</strong>
    </th>
    {Object.keys(totalOperatingExpenses.data).map(round_number => {
      let data1 = totalOperatingExpenses.data[round_number];
      let data2 = grossProfits.data[round_number];
      return (
        <td key={round_number}>
          <PriceInMillions
            baseClassName="text-center"
            price={data1.value + data2.value}
          />
        </td>
      );
    })}
  </tr>
);

/**
 * Table row title.
 */

const TotalTitleHeader = ({ title, rounds }) => (
  <tr className="text-center">
    <th className="sub-title title-sky-blue">{title}</th>
    {rounds.map((round, i) => (
      <td key={i}>
        <div className="row flex">
          <div className="col-xs-6" />
          <div className="col-xs-6" />
          <div className="col-xs-6" />
        </div>
      </td>
    ))}
  </tr>
);

/**
 *
 * FNAnalysisFNStatementsIncomeStatement
 *
 */
class FNAnalysisFNStatementsIncomeStatement extends Component {
  constructor(props) {
    super(props);

    this.state = {
      roundInUrl: props.roundInUrl,
      incomeStatement: {
        costOfSales: {
          name: "Cost of Sales",
          data: {}
        },
        revenues: {
          name: "Net Revenues",
          data: {}
        },
        grossProfits: {
          name: "Gross Profit",
          data: {}
        },
        RnDCosts: {
          name: "R&D Costs",
          data: {}
        },
        holdingCosts: {
          name: "Holding Costs",
          data: {}
        },
        promotionCosts: {
          name: "Promotion Costs",
          data: {}
        },
        sellingCosts: {
          name: "Selling Costs",
          data: {}
        },
        gnACosts: {
          name: "Other G&A Costs",
          data: {}
        },
        depExpense: {
          name: "Depreciation Expense",
          data: {}
        },
        incomingBeforeTaxes: {
          name: "Income Before Taxes",
          data: {}
        },
        taxes: {
          name: "Taxes",
          data: {}
        },
        totalOperatingExpenses: {
          name: "Total Operating Costs",
          data: {}
        },
        totalNonOperatingCost: {
          name: "Total Non-Operating Costs",
          data: {}
        },
        gainOrLossoFSales: {
          name: "Gain or Loss of Sales",
          data: {}
        },
        interestExpense: {
          name: "Interest Expense",
          data: {}
        },
        otherNonOperatingCosts: {
          name: "Other Non-Operating Costs",
          data: {}
        },
        netIncome: {
          name: "Net Income",
          data: {}
        }
      }
    };
  }

  componentWillReceiveProps(nextProps) {
    if (this.state.roundInUrl !== nextProps.roundInUrl) {
      this.setState(
        {
          roundInUrl: nextProps.roundInUrl
        },
        () => {
          this.loadState();
        }
      );
    }
  }

  getIncomeStatement() {
    const base_url = `${process.env.REACT_APP_API_URL}team/report/income_statement/`;
    let url = `${base_url}?round_lte=${this.props.currentRound}`;
    if (
      this.state.roundInUrl !== null &&
      this.state.roundInUrl !== this.props.currentRound
    ) {
      url = `${base_url}?round_lte=${this.state.roundInUrl}`;
    }

    return getRequest(url);
  }

  loadState = () => {
    if (this.props.rounds.length === 0) {
      this.timer = setTimeout(this.loadState, 1000);
      return;
    }

    this.clearData();
    this.fetchData();
  };

  clearData() {
    let incomeStatement = { ...this.state.incomeStatement };
    let headRounds = this.headRounds;

    for (let key in incomeStatement) {
      incomeStatement[key].data = {};
      headRounds.forEach(round => {
        incomeStatement[key].data[round.number] = {
          value: 0
        };
      });
    }

    this.setState({
      incomeStatement
    });
  }

  fetchData() {
    this.getIncomeStatement()
      .then(res => {
        const incomeStatement = { ...this.state.incomeStatement };

        for (let key in incomeStatement) {
          res.body.forEach(item => {
            let round_id = item.round;
            let round = this.props.rounds.find(i => i.id === round_id);
            let round_number = round.number;
            const hash = incomeStatement[key].name;

            if (item[hash]) {
              incomeStatement[key].data[round_number].value = item[hash];
            }
          });
        }

        this.setState({
          incomeStatement
        });
      })
      .catch(err => {
        logError(
          err,
          "FNAnalysisFNStatementsIncomeStatement",
          "componentDidMount"
        );
      });
  }

  componentDidMount() {
    this.loadState();
  }

  componentWillUnmount() {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }

  get headRounds() {
    const { roundInUrl } = this.state;
    const { currentRound, rounds } = this.props;

    let current_round = currentRound;
    if (roundInUrl !== null && roundInUrl !== currentRound) {
      current_round = roundInUrl;
    }

    return rounds.filter(i => i.number <= current_round);
  }

  render() {
    const {
      costOfSales,
      revenues,
      grossProfits,
      RnDCosts,
      holdingCosts,
      promotionCosts,
      sellingCosts,
      gnACosts,
      incomingBeforeTaxes,
      taxes,
      totalNonOperatingCost,
      interestExpense,
      totalOperatingExpenses,
      gainOrLossoFSales,
      otherNonOperatingCosts,
      netIncome,
      depExpense
    } = this.state.incomeStatement;
    const headRounds = this.headRounds;

    return (
      <div className="panel-dashboard-white-full">
        <h5 className="title-sky-blue">
          Income Statement
          <HelpIconButtonWithTooltipAndTypeformModal
            iconSize="sm"
            {...TypeformContent.INCOME_STATEMENT}
          />
        </h5>
        <div className="table-responsive fn-results-table">
          <table className="table border-analysis mb-20">
            <TableHead rounds={headRounds} />
            {Object.keys(costOfSales.data).length > 0 ||
            Object.keys(revenues.data).length > 0 ||
            Object.keys(grossProfits.data).length > 0 ? (
              <tbody>
                <TableRowContents incomeStatement={revenues} />
                <TableRowContents incomeStatement={costOfSales} />
                <TableRowContentsFooter incomeStatement={grossProfits} />
                <TotalTitleHeader
                  title="Operating Expenses"
                  rounds={headRounds}
                />
                <TableRowContents incomeStatement={RnDCosts} />
                <TableRowContents incomeStatement={holdingCosts} />
                <TableRowContents incomeStatement={promotionCosts} />
                <TableRowContents incomeStatement={sellingCosts} />
                <OtherGnACosts
                  gnaCosts={gnACosts}
                  depExpense={depExpense}
                  name="Other G&A Costs"
                />
                <TableRowContentsFooter
                  incomeStatement={totalOperatingExpenses}
                />
                <OperatingProfitComponent
                  totalOperatingExpenses={totalOperatingExpenses}
                  grossProfits={grossProfits}
                />
                <TotalTitleHeader
                  title="Non-Operating Costs"
                  rounds={headRounds}
                />
                <TableRowContents incomeStatement={interestExpense} />
                <TableRowContents incomeStatement={gainOrLossoFSales} />
                <TableRowContents incomeStatement={otherNonOperatingCosts} />
                <TotalComponent incomeStatement={totalNonOperatingCost} />
                <TotalTitleHeader title="Profits" rounds={headRounds} />
                <TableRowContents incomeStatement={incomingBeforeTaxes} />
                <TableRowContents incomeStatement={taxes} />
                <TotalComponent
                  incomeStatement={netIncome}
                  title="Net Profits"
                />
              </tbody>
            ) : (
              <tbody className="empty-table" />
            )}
          </table>
        </div>
      </div>
    );
  }
}

export default FNAnalysisFNStatementsIncomeStatement;



// WEBPACK FOOTER //
// ./src/containers/FNAnalysisFNStatementsIncomeStatement.js